import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Container,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { commonButtonProps, commonTextFieldProps } from "../../../utils/styles";
import StopsTable from "../../../pages/masterData/route/StopsTable";
import { useToast } from "../../../components/Toast";
import NavigationHeader from "../../../components/NavigationHeader";
import { useNavigate, useParams } from "react-router-dom";
import { routeServiceApi } from "./apiService";
import { Loading } from "../../../components/Loading";

const validationSchema = Yup.object().shape({
  routes: Yup.array().of(
    Yup.object().shape({
      fromLocation: Yup.string().required("From location is required"),
      toLocation: Yup.string().required("To location is required"),
      stops: Yup.array()
        .of(
          Yup.object().shape({
            stopNumber: Yup.number().required("Stop number is required"),
            stopName: Yup.string().required("Stop name is required"),
          })
        )
        .min(1, "At least one stop is required"),
    })
  ),
});

const AddRoute = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<any>(null);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;

  const { routeId } = useParams();

  const defaultInitialValues = {
    routes: [
      {
        id: null,
        routeName: null,
        fromLocation: "",
        toLocation: "",
        routeManagerName: "",
        routeManagerNumber: "",
        companyId,
        createdAt: null,
        updatedAt: null,
        deletedAt: null,
        isEdited: false,
        isNew: true,
        stops: [],
      },
    ],
  };

  const fetchRoute = async () => {
    setIsLoading(true);
    try {
      const route = await routeServiceApi.getRouteById(routeId!);

      const editValues = {
        routes: [
          {
            id: route.id,
            routeName: route.routeName,
            fromLocation: route.fromLocation,
            toLocation: route.toLocation,
            routeManagerName: route.routeManagerName || "",
            routeManagerNumber: route.routeManagerNumber || "",
            companyId,
            createdAt: route.createdAt,
            updatedAt: route.updatedAt,
            deletedAt: route.deletedAt,
            isEdited: true,
            isNew: false,
            stops: route.stops || [],
          },
        ],
      };

      setInitialFormValues(editValues);
    } catch (error) {
      console.error("Error fetching route:", error);
      showToast("Error fetching route", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (routeId) {
      fetchRoute();
    } else {
      setInitialFormValues(defaultInitialValues);
    }
  }, [routeId]);

  const handleSubmit = async (values: any) => {
    const routeData = values.routes[0];
    try {
      setIsSubmitting(true);
      if (routeId) {
        const response = await routeServiceApi.updateRoute(routeId, routeData);
        showToast("Route updated successfully", "success");
      } else {
        const response = await routeServiceApi.createRoute(routeData);
        showToast("Route created successfully", "success");
      }
      navigate(-1);
    } catch (error) {
      console.error("Error saving route:", error);
      showToast("Error saving route", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading || !initialFormValues) {
    return <Loading message="Loading..." />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <NavigationHeader
        title={routeId ? "Edit Route" : "Add New Route"}
        breadcrumbs={[
          {
            label: `Routes`,
            path: `/master-data/routes`,
          },
          {
            label: routeId ? "Edit Route" : "Add Route",
          },
        ]}
      />

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 3, fontSize: "1.1rem" }}>
          Route Information
        </Typography>

        <Formik
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            errors,
            touched,
            handleChange,
            values,
            setFieldValue,
            isValid,
            dirty,
          }) => (
            <Form>
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={2}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="routes[0].routeName"
                      label="Route Name"
                      variant="outlined"
                      onChange={handleChange}
                      value={values.routes[0].routeName}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="routes[0].fromLocation"
                      label="From Location"
                      variant="outlined"
                      onChange={handleChange}
                      value={values.routes[0].fromLocation}
                      error={false}
                      helperText=""
                      {...commonTextFieldProps}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="routes[0].toLocation"
                      label="To Location"
                      variant="outlined"
                      onChange={handleChange}
                      value={values.routes[0].toLocation}
                      error={false}
                      helperText=""
                      {...commonTextFieldProps}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="routes[0].routeManagerName"
                      label="Route Manager Name"
                      variant="outlined"
                      onChange={handleChange}
                      value={values.routes[0].routeManagerName}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="routes[0].routeManagerNumber"
                      label="Route Manager Number"
                      variant="outlined"
                      onChange={handleChange}
                      value={values.routes[0].routeManagerNumber}
                      inputProps={{
                        maxLength: 10,
                      }}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mb: 2, fontSize: "1rem" }}>
                  Route Stops
                </Typography>

                <StopsTable
                  stops={values.routes[0].stops}
                  routeId={values.routes[0].id || ""}
                  isEditing={true}
                  index={0}
                  setFieldValue={setFieldValue}
                  values={values}
                  handleChange={handleChange}
                  showTime={routeId ? false : true}
                />

                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    {...commonButtonProps}
                    color="error"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    {...commonButtonProps}
                    disabled={isSubmitting || !isValid || !dirty}
                  >
                    {isSubmitting
                      ? "Saving..."
                      : routeId
                      ? "Update Route"
                      : "Save Route"}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default AddRoute;
